import { useRef, useEffect, useCallback } from "react";
import { EditorView } from "prosemirror-view";
import { EditorState } from "prosemirror-state";
import { history, undo, redo } from "prosemirror-history";
import { baseKeymap } from "prosemirror-commands";
import { keymap } from "prosemirror-keymap";
import type { NLSDocument } from "../documents/types.ts";
import type { ViewspecState } from "../viewspecs/types.ts";
import { documentToDoc } from "../editor/state.ts";
import { viewspecPlugin, viewspecKey } from "../editor/plugins/viewspecs.ts";
import { hyperlinkPlugin } from "../editor/plugins/hyperlinks.ts";
import { addressingPlugin } from "../editor/plugins/addressing.ts";
import { nlsKeymap, handleTab, handleShiftTab } from "../editor/keymap.ts";
import { sidSyncPlugin } from "../editor/plugins/sid-sync.ts";
import { parseViewspecCodes } from "../viewspecs/codes.ts";
import type { NavigateTarget } from "../editor/plugins/hyperlinks.ts";

interface NLSEditorProps {
  document: NLSDocument;
  viewspecs: ViewspecState;
  onViewspecChange: (vs: Partial<ViewspecState>) => void;
  onNavigate: (path: string) => void;
  onCursorMove?: (sid: string) => void;
  editorViewRef?: React.MutableRefObject<EditorView | null>;
  focusCommandBar: () => void;
  executeCommand: (name: string) => void;
}

export function NLSEditor({
  document,
  viewspecs,
  onViewspecChange,
  onNavigate,
  onCursorMove,
  editorViewRef,
  focusCommandBar,
  executeCommand,
}: NLSEditorProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Handle link navigation
  const handleNavigate = useCallback(
    (target: NavigateTarget) => {
      if (target.documentId !== document.id) {
        const path = target.sid
          ? `/doc/${target.documentId}#${target.sid}`
          : `/doc/${target.documentId}`;
        onNavigate(path);
      }
      if (target.viewspec) {
        onViewspecChange(parseViewspecCodes(target.viewspec));
      }
    },
    [document.id, onNavigate, onViewspecChange]
  );

  // Create editor
  useEffect(() => {
    if (!mountRef.current) return;

    const doc = documentToDoc(document.root);

    const plugins = [
      nlsKeymap({
        focusCommandBar,
        setViewspecs: onViewspecChange,
        navigate: onNavigate,
        executeCommand,
      }),
      keymap({ "Mod-z": undo, "Shift-Mod-z": redo }),
      sidSyncPlugin(),
      viewspecPlugin(viewspecs),
      hyperlinkPlugin(handleNavigate),
      addressingPlugin(),
      history(),
      keymap(baseKeymap),
    ];

    const state = EditorState.create({ doc, plugins });

    const view = new EditorView(mountRef.current, {
      state,
      handleDOMEvents: {
        keydown(view, event) {
          if (event.key === "Tab") {
            event.preventDefault();
            if (event.shiftKey) {
              handleShiftTab(view.state, view.dispatch.bind(view));
            } else {
              handleTab(view.state, view.dispatch.bind(view));
            }
            return true;
          }
          return false;
        },
      },
      dispatchTransaction(tr) {
        const newState = view.state.apply(tr);
        view.updateState(newState);

        // Notify cursor position changes
        if (onCursorMove && tr.selectionSet) {
          const { $from } = newState.selection;
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === "statement") {
              onCursorMove(node.attrs.sid as string);
              break;
            }
          }
        }
      },
    });

    viewRef.current = view;
    if (editorViewRef) {
      editorViewRef.current = view;
    }

    return () => {
      view.destroy();
      viewRef.current = null;
      if (editorViewRef) {
        editorViewRef.current = null;
      }
    };
  }, [document.id]); // Only recreate when document changes

  // Sync viewspec changes to ProseMirror plugin
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const tr = view.state.tr.setMeta(viewspecKey, viewspecs);
    view.dispatch(tr);
  }, [viewspecs]);

  return (
    <div
      ref={mountRef}
      className="nls-editor-mount"
      style={{ height: "100%", overflow: "auto" }}
    />
  );
}
